import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Save, Calendar, Users, Tag, Link2, Plus, Trash2, Loader2, GitBranch, Lock, Pencil, Eye, AlertTriangle, Check, Github, RefreshCw, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { MarkdownEditor } from '../ui/markdown-editor';
import { DatePickerWithMarkers } from '../ui/date-picker-with-markers';
import { featuresApi, productTagsApi, epicsApi, releasesApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags, FEATURE_IDS } from '../../hooks/useFeatureFlags';
import { ModalZIndexProvider } from '../../contexts/ModalZIndexContext';
import { useTimezone } from '../../hooks/useTimezone';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import SplitFeatureModal from './SplitFeatureModal';
import { NotesPanel, AttachmentsPanel } from '../shared';
import DevLogTab from './DevLogTab';
import { UnifiedCoPilot } from '../copilot';
import KpiSubscriptionTab from '../shared/KpiSubscriptionTab';

const EFFORT_SIZES = ['XS', 'S', 'M', 'L', 'XL'];
const PRIORITY_LEVELS = ['Critical', 'High', 'Medium', 'Low'];
const DEFAULT_STATUSES = ['Backlog', 'In Progress', 'Review', 'Done'];

// Editable Link Row - inline edit for title and tags on reference links
const EditableLinkRow = ({ link, index, isEditing, onUpdate, onRemove, colors }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(link.title || '');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState(link.tags || []);

  const handleSave = () => {
    onUpdate(index, { title, tags });
    setEditing(false);
  };

  const handleCancel = () => {
    setTitle(link.title || '');
    setTags(link.tags || []);
    setEditing(false);
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
      setTagInput('');
    }
  };

  const removeTag = (t) => setTags(tags.filter(x => x !== t));

  if (editing) {
    return (
      <div className={cn("p-3 rounded-lg border-2 border-blue-200 dark:border-blue-800 space-y-3", colors.bgTertiary)}>
        <div className="flex items-center gap-2">
          <Link2 className={cn("w-4 h-4 flex-shrink-0", colors.textMuted)} />
          <a href={link.url} target="_blank" rel="noopener noreferrer" className={cn("text-xs truncate", colors.textAccent)}>
            {link.url}
          </a>
          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleCancel}><X className="w-3.5 h-3.5" /></Button>
            <Button size="sm" className="h-7 w-7 p-0 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSave}><Check className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        <div>
          <Label className={cn("text-xs", colors.textSecondary)}>Title</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={link.url} className={cn("mt-1 text-sm", colors.bgInput, colors.borderPrimary, colors.textPrimary)} />
        </div>
        <div>
          <Label className={cn("text-xs", colors.textSecondary)}>Tags</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {tags.map((t, i) => (
              <Badge key={i} variant="secondary" className="text-xs gap-1">
                {t}
                <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(t)} />
              </Badge>
            ))}
          </div>
          <div className="flex gap-2 mt-1.5">
            <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Add tag..." className={cn("text-sm", colors.bgInput, colors.borderPrimary, colors.textPrimary)} />
            <Button variant="outline" size="icon" onClick={addTag} className={cn("h-9 w-9 flex-shrink-0 border", colors.borderPrimary)}>
              <Tag className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Non-editing view
  const displayTitle = link.title || link.url;
  return (
    <div className={cn("flex items-center gap-3 p-3 rounded-lg group", colors.bgTertiary)}>
      <Link2 className={cn("w-4 h-4 flex-shrink-0", colors.textMuted)} />
      <div className="flex-1 min-w-0">
        <a href={link.url} target="_blank" rel="noopener noreferrer" className={cn("text-sm truncate block", colors.textAccent)} title={link.url}>
          {displayTitle}
        </a>
        {link.title && (
          <p className={cn("text-xs truncate", colors.textMuted)} title={link.url}>{link.url}</p>
        )}
        {link.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {link.tags.map((t, i) => (
              <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 h-4">{t}</Badge>
            ))}
          </div>
        )}
      </div>
      {isEditing && (
        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-500 hover:text-blue-600" onClick={() => setEditing(true)}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => onRemove(index)}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};


/**
 * FeatureDetailModal
 * 
 * Displays and allows editing of feature details.
 * 
 * STANDALONE MODE:
 * When `standalone={true}`, this modal operates independently without requiring
 * parent callbacks. Used by Executive AI to open features in-place without
 * navigating away. In standalone mode:
 * - onUpdate callback is ignored (API is called directly, toast shown)
 * - Modal closes automatically after save/delete
 * - No parent state refresh needed
 *
 * Follows Epic pattern for callbacks:
 * - onSave(savedFeature) - called on save, triggers entity refresh
 * - onDelete() - called on delete, triggers collection refresh
 * - onSplitComplete() - called on split, triggers collection refresh
 */
const FeatureDetailModal = ({ 
  isOpen,
  feature, 
  onSave,  // Called with saved feature data for entity refresh
  onDelete,  // Called on delete for collection refresh
  onSplitComplete: onSplitCompleteCallback,  // Called on split for collection refresh
  onUpdate,  // DEPRECATED: Use onSave instead (kept for backward compat)
  onClose, 
  personas = [],
  teamMembers = [],
  productContext = {},
  epicContext = {},  // { epic, siblingFeatures }
  goals = [],
  releases = [],  // Available releases for this product
  statuses = DEFAULT_STATUSES,  // Custom statuses from product kanban columns
  standalone = false,  // When true, modal operates independently without parent callbacks
  zIndex = 50,  // Z-index for modal stacking
  suppressChildModals = false,  // When true, hides AI copilot and prevents entity edit modals from launching as children
  parentRefresh,  // Parent's refresh callbacks — when present, this modal is a child and calls these after save
}) => {
  const { getThemeColors } = useSafeMode();
  const { canCrudProducts, canUseAI, isCollaborator } = usePermissions();
  const { isEnabled } = useFeatureFlags();
  const { user } = useAuth();
  const { formatDate, formatTime, formatDateTime } = useTimezone();
  const colors = getThemeColors();
  
  // Permission flags
  const canEdit = canCrudProducts;
  const canUseAICopilot = canUseAI;
  
  // Check if feature is locked (in production)
  const isInProduction = feature?.in_production === true;
  
  // Show AI Copilot only for existing features in edit mode (not for in-production features)
  const showAICopilot = canUseAICopilot && !suppressChildModals && feature?.feature_id && !isInProduction;
  
  // Edit mode state - defaults to View Mode for non-Backlog status
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Helper: Get contrast color for tag text
  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#ffffff';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#1f2937' : '#ffffff';
  };
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    user_story: '',
    requirements: [],
    priority_level: 'Medium',
    status: 'Backlog',
    release_id: '',  // Parking Lot when empty
    persona_ids: [],  // Multiple personas per feature
    assignee_id: '',
    due_date: '',
    effort_size: '',
    tag_ids: [],
    links: [],
    epic_id: '',
    execution_type: 'engineering',
    is_blocked: false,
    blocker_reason: '',
    blocked_at: null,
    blocked_by: null,
    blocked_by_name: null,
    // Outcome-Driven Model: KPI alignments using compound key format "goal_id:kpi_id"
    kpi_alignments: []  // [{kpi_key, hypothesis}]
  });
  
  // Track original epic and KPI alignments for epic change handling
  const [originalEpicId, setOriginalEpicId] = useState('');
  const [originalKpiAlignments, setOriginalKpiAlignments] = useState([]);
  const [availableKpisForEpic, setAvailableKpisForEpic] = useState(null); // null = use default fetch, object = override
  
  // Computed: Should show edit mode toggle? (PM/Admin only when status !== Backlog AND not in production)
  const showEditToggle = canEdit && formData.status !== 'Backlog' && !isInProduction;
  
  // Computed: Parent deadlines for calendar (Epic's target_date)
  const parentDeadlines = useMemo(() => {
    const deadlines = [];
    // Get the current epic from epicContext or find it by epic_id
    const currentEpic = epicContext?.epic || epicContext?.epics?.find(e => e.epic_id === formData.epic_id);
    if (currentEpic?.target_date) {
      deadlines.push({
        date: currentEpic.target_date,
        label: `Epic: ${currentEpic.title || 'Deadline'}`,
      });
    }
    return deadlines;
  }, [epicContext?.epic, epicContext?.epics, formData.epic_id]);
  
  // Get blocker user name
  const getBlockedByName = () => {
    if (!formData.blocked_by) return null;
    
    // Check if it's the current user
    if (user?.user_id === formData.blocked_by) {
      return user?.name || user?.email || 'You';
    }
    
    // Check teamMembers by user_id or member_id
    const member = teamMembers?.find(m => 
      m.user_id === formData.blocked_by || 
      m.member_id === formData.blocked_by
    );
    if (member?.name) return member.name;
    
    // If blocked_by_name was stored (from MCP), use it
    if (formData.blocked_by_name) return formData.blocked_by_name;
    
    return null;
  };
  // Computed: Is currently in editing state?
  // PM/Admin: Edit mode if status is Backlog OR if edit toggle is ON
  // Contributor: Never in edit mode for general fields
  // In Production: Always locked - never editable
  const isEditing = !isInProduction && canEdit && (formData.status === 'Backlog' || isEditMode);
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showTicketSyncModal, setShowTicketSyncModal] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState(null); // 'save' or 'saveAndClose'
  
  // Tags
  const [availableTags, setAvailableTags] = useState([]);
  const [initiativeTags, setInitiativeTags] = useState([]);
  const [initiativeTagsGrouped, setInitiativeTagsGrouped] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [showTagModal, setShowTagModal] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [tagModalTab, setTagModalTab] = useState('product'); // 'product' | 'initiative'
  const [selectedInitiativeFilter, setSelectedInitiativeFilter] = useState('all');
  
  // Acceptance criteria
  const [newRequirement, setNewCriterion] = useState('');
  
  // Links
  const [newLink, setNewLink] = useState('');
  
  // Tab counts
  const [attachmentCount, setAttachmentCount] = useState(0);
  const [notesCount, setNotesCount] = useState(0);
  
  // Feature-specific modals
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitData, setSplitData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('key-attributes');
  
  /**
   * Handle Epic change with cascading KPI updates
   * 
   * When the epic changes:
   * 1. If reverting to original epic → restore original KPI alignments
   * 2. If changing to new epic → clear KPI alignments and fetch new available KPIs
   */
  const handleEpicChange = useCallback(async (newEpicId) => {
    // Update the epic_id in form
    setFormData(prev => ({ ...prev, epic_id: newEpicId }));
    setHasChanges(true);
    
    // Check if reverting to original epic
    if (newEpicId === originalEpicId) {
      // Restore original KPI alignments
      setFormData(prev => ({
        ...prev,
        epic_id: newEpicId,
        kpi_alignments: originalKpiAlignments
      }));
      setAvailableKpisForEpic(null); // Reset to default fetch
      return;
    }
    
    // Changing to a different epic - clear KPI alignments
    setFormData(prev => ({
      ...prev,
      epic_id: newEpicId,
      kpi_alignments: []
    }));
    
    // Fetch available KPIs for the new epic
    if (newEpicId) {
      try {
        const response = await epicsApi.getInheritedKpis(newEpicId);
        setAvailableKpisForEpic(response.data);
      } catch (err) {
        console.error('Failed to fetch KPIs for epic:', err);
        toast.error('Failed to load available KPIs for selected epic');
        setAvailableKpisForEpic({ inherited_kpis: [], by_goal: [] });
      }
    } else {
      // No epic selected - no KPIs available
      setAvailableKpisForEpic({ inherited_kpis: [], by_goal: [] });
    }
  }, [originalEpicId, originalKpiAlignments]);

  /**
   * Recalculate available KPIs when tags change
   * 
   * Features can get KPIs from two sources:
   * 1. Parent Epic's subscribed KPIs
   * 2. Tag-following Initiatives (via tags)
   * 
   * When tags change, we need to merge KPIs from both sources.
   * Uses the goals prop (already loaded from parent) for efficient lookup.
   */
  const tagIdsKey = JSON.stringify(formData.tag_ids?.slice().sort() || []);
  
  useEffect(() => {
    // Build a goalMap from the passed-down goals prop for efficient lookup
    const goalMap = {};
    goals.forEach(goal => {
      goalMap[goal.goal_id] = goal;
    });
    
    // Helper function to build KPI entries from kpi_keys
    const buildKpiEntries = (kpiKeys, source) => {
      const kpis = [];
      const byGoalMap = {};
      
      for (const kpiKey of kpiKeys) {
        const [goalId, kpiId] = kpiKey.split(':');
        const goal = goalMap[goalId];
        if (!goal) continue;
        
        const kpi = goal.kpis?.find(k => k.kpi_id === kpiId);
        if (!kpi) continue;
        
        const kpiEntry = {
          kpi_key: kpiKey,
          kpi_id: kpiId,
          goal_id: goalId,
          goal_title: goal.title || 'Untitled Goal',
          metric_name: kpi.metric_name || 'Unnamed KPI',
          metric_format: kpi.metric_format || 'number',
          direction: kpi.direction || 'higher_is_better',
          current_value: kpi.current_value,
          target_value: kpi.target_value,
          is_subscribed: false,
          source: source
        };
        
        kpis.push(kpiEntry);
        
        // Group by goal
        if (!byGoalMap[goalId]) {
          byGoalMap[goalId] = {
            goal_id: goalId,
            goal_title: goal.title || 'Untitled Goal',
            goal_status: goal.status || 'active',
            goal_target_date: goal.target_date,
            kpis: []
          };
        }
        byGoalMap[goalId].kpis.push(kpiEntry);
      }
      
      return { kpis, byGoalMap };
    };
    
    // Get KPIs from tag-following Initiatives
    const tagKpiKeys = new Set();
    for (const tagId of formData.tag_ids || []) {
      const group = initiativeTagsGrouped.find(g => 
        g.tags?.some(t => t.tag_id === tagId)
      );
      if (group?.subscribed_kpi_ids) {
        group.subscribed_kpi_ids.forEach(kpiKey => tagKpiKeys.add(kpiKey));
      }
    }
    
    // Get KPIs from Epic (look up Epic from epicContext.epics)
    const epicKpiKeys = new Set();
    if (formData.epic_id && epicContext?.epics) {
      const epic = epicContext.epics.find(e => e.epic_id === formData.epic_id);
      if (epic?.subscribed_kpi_ids) {
        epic.subscribed_kpi_ids.forEach(kpiKey => epicKpiKeys.add(kpiKey));
      }
    }
    
    // If no KPIs from either source, don't override
    if (tagKpiKeys.size === 0 && epicKpiKeys.size === 0) {
      // Only set empty if there's no epic (let backend fetch handle it)
      if (!formData.epic_id) {
        setAvailableKpisForEpic({ inherited_kpis: [], by_goal: [] });
      }
      return;
    }
    
    // Build KPI entries for both sources
    const { kpis: tagKpis, byGoalMap: tagByGoal } = buildKpiEntries(tagKpiKeys, 'tag');
    const { kpis: epicKpis, byGoalMap: epicByGoal } = buildKpiEntries(epicKpiKeys, 'epic');
    
    // Merge Epic and Tag KPIs (dedupe by kpi_key, Epic takes precedence)
    const seenKeys = new Set();
    const mergedKpis = [];
    const mergedByGoal = {};
    
    // Add Epic KPIs first
    for (const kpi of epicKpis) {
      seenKeys.add(kpi.kpi_key);
      mergedKpis.push(kpi);
    }
    
    // Add Tag KPIs that aren't already included
    for (const kpi of tagKpis) {
      if (!seenKeys.has(kpi.kpi_key)) {
        seenKeys.add(kpi.kpi_key);
        mergedKpis.push(kpi);
      }
    }
    
    // Merge by_goal structures
    // Start with epic goals
    Object.entries(epicByGoal).forEach(([goalId, goalGroup]) => {
      mergedByGoal[goalId] = { ...goalGroup, kpis: [...goalGroup.kpis] };
    });
    
    // Add tag KPIs to existing goals or create new goal groups
    Object.entries(tagByGoal).forEach(([goalId, goalGroup]) => {
      if (mergedByGoal[goalId]) {
        // Goal exists, add KPIs that aren't already there
        const existingKeys = new Set(mergedByGoal[goalId].kpis.map(k => k.kpi_key));
        goalGroup.kpis.forEach(kpi => {
          if (!existingKeys.has(kpi.kpi_key)) {
            mergedByGoal[goalId].kpis.push(kpi);
          }
        });
      } else {
        // New goal from tags
        mergedByGoal[goalId] = { ...goalGroup, kpis: [...goalGroup.kpis] };
      }
    });
    
    setAvailableKpisForEpic({
      inherited_kpis: mergedKpis,
      by_goal: Object.values(mergedByGoal),
      total_available: mergedKpis.length
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagIdsKey, formData.epic_id, initiativeTagsGrouped, epicContext?.epics, goals]);

  // Handle AI Co-Pilot apply updates
  const handleApplyUpdates = useCallback((updates) => {
    if (!updates) return;
    
    Object.entries(updates).forEach(([field, value]) => {
      // =====================================================================
      // SPECIAL OPERATIONS (prefixed with _) - from embedded entity actions
      // =====================================================================
      
      // Handle _updateRequirements - update existing requirements by item_id
      if (field === '_updateRequirements' && Array.isArray(value)) {
        setFormData(prev => {
          const updatedReqs = (prev.requirements || []).map(req => {
            const update = value.find(u => u.item_id === req.req_id);
            if (update) {
              return {
                ...req,
                text: update.text !== undefined ? update.text : req.text,
                is_completed: update.is_completed !== undefined ? update.is_completed : req.is_completed
              };
            }
            return req;
          });
          return { ...prev, requirements: updatedReqs };
        });
        return;
      }
      
      // Handle _deleteRequirements - remove requirements by item_id
      if (field === '_deleteRequirements' && Array.isArray(value)) {
        setFormData(prev => {
          const filteredReqs = (prev.requirements || []).filter(req => !value.includes(req.req_id));
          return { ...prev, requirements: filteredReqs };
        });
        return;
      }
      
      // Handle _updateKpiAlignments - update existing KPI alignments by kpi_key
      if (field === '_updateKpiAlignments' && Array.isArray(value)) {
        setFormData(prev => {
          const updatedKpis = (prev.kpi_alignments || []).map(kpi => {
            const update = value.find(u => u.kpi_key === kpi.kpi_key);
            if (update) {
              return {
                ...kpi,
                hypothesis: update.hypothesis !== undefined ? update.hypothesis : kpi.hypothesis
              };
            }
            return kpi;
          });
          return { ...prev, kpi_alignments: updatedKpis };
        });
        return;
      }
      
      // Handle _deleteKpiAlignments - remove KPI alignments by item_id
      if (field === '_deleteKpiAlignments' && Array.isArray(value)) {
        setFormData(prev => {
          const filteredKpis = (prev.kpi_alignments || []).filter(kpi => !value.includes(kpi.align_id));
          return { ...prev, kpi_alignments: filteredKpis };
        });
        return;
      }
      
      // Handle _link_persona_ids - append persona IDs
      if (field === '_link_persona_ids' && Array.isArray(value)) {
        setFormData(prev => {
          const existingIds = prev.persona_ids || [];
          const mergedIds = [...new Set([...existingIds, ...value])];
          return { ...prev, persona_ids: mergedIds };
        });
        return;
      }
      
      // Handle _unlink_persona_ids - remove persona IDs
      if (field === '_unlink_persona_ids' && Array.isArray(value)) {
        setFormData(prev => {
          const filteredIds = (prev.persona_ids || []).filter(id => !value.includes(id));
          return { ...prev, persona_ids: filteredIds };
        });
        return;
      }
      
      // Handle _link_tag_ids - append tag IDs
      if (field === '_link_tag_ids' && Array.isArray(value)) {
        setFormData(prev => {
          const existingIds = prev.tag_ids || [];
          const mergedIds = [...new Set([...existingIds, ...value])];
          return { ...prev, tag_ids: mergedIds };
        });
        return;
      }
      
      // Handle _unlink_tag_ids - remove tag IDs
      if (field === '_unlink_tag_ids' && Array.isArray(value)) {
        setFormData(prev => {
          const filteredIds = (prev.tag_ids || []).filter(id => !value.includes(id));
          return { ...prev, tag_ids: filteredIds };
        });
        return;
      }
      
      // =====================================================================
      // STANDARD FIELD UPDATES
      // =====================================================================
      
      // Handle persona_ids - map persona names to IDs if needed and merge with existing
      if (field === 'persona_ids') {
        let newPersonaIds = value;
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
          // Check if these are names that need to be mapped to IDs
          newPersonaIds = value.map(nameOrId => {
            // Already an ID
            if (nameOrId.startsWith('persona_')) return nameOrId;
            // Try to find by name
            const found = personas.find(p => p.name.toLowerCase() === nameOrId.toLowerCase());
            return found?.persona_id || nameOrId;
          }).filter(Boolean);
        }
        // Merge with existing persona_ids (avoid duplicates)
        setFormData(prev => {
          const existingIds = prev.persona_ids || [];
          const mergedIds = [...new Set([...existingIds, ...newPersonaIds])];
          return { ...prev, persona_ids: mergedIds };
        });
      }
      // Handle requirements - ALWAYS append to preserve existing requirements
      else if (field === 'requirements') {
        if (Array.isArray(value)) {
          // Convert new requirements to proper format
          const newReqs = value.map(r => typeof r === 'string' ? { text: r, is_completed: false } : r);
          
          setFormData(prev => {
            const existingReqs = prev.requirements || [];
            
            // Filter out duplicates - check if requirement text already exists
            const existingTexts = new Set(existingReqs.map(r => 
              (typeof r === 'string' ? r : r.text || '').toLowerCase().trim()
            ));
            
            const uniqueNewReqs = newReqs.filter(r => {
              const reqText = (typeof r === 'string' ? r : r.text || '').toLowerCase().trim();
              return reqText && !existingTexts.has(reqText);
            });
            
            // Append unique new requirements to existing ones
            return {
              ...prev,
              requirements: [...existingReqs, ...uniqueNewReqs]
            };
          });
        }
      }
      // Handle kpi_alignments - append new alignments
      else if (field === 'kpi_alignments') {
        if (Array.isArray(value)) {
          setFormData(prev => {
            const existingKpis = prev.kpi_alignments || [];
            
            // Filter out duplicates by kpi_key
            const existingKeys = new Set(existingKpis.map(k => k.kpi_key?.toLowerCase().trim()));
            
            const uniqueNewKpis = value.filter(k => {
              const key = k.kpi_key?.toLowerCase().trim();
              return key && !existingKeys.has(key);
            });
            
            return {
              ...prev,
              kpi_alignments: [...existingKpis, ...uniqueNewKpis]
            };
          });
        }
      }
      // Handle status - normalize to valid values
      else if (field === 'status' && value) {
        const validStatuses = statuses.map(s => s.id);
        const normalizedStatus = value;
        if (validStatuses.includes(normalizedStatus)) {
          setFormData(prev => ({ ...prev, status: normalizedStatus }));
        }
      }
      // Handle priority_level
      else if (field === 'priority_level' && value) {
        const validPriorities = ['Critical', 'High', 'Medium', 'Low'];
        if (validPriorities.includes(value)) {
          setFormData(prev => ({ ...prev, priority_level: value }));
        }
      }
      // Handle effort_size
      else if (field === 'effort_size' && value) {
        const validSizes = ['XS', 'S', 'M', 'L', 'XL'];
        if (validSizes.includes(value)) {
          setFormData(prev => ({ ...prev, effort_size: value }));
        }
      }
      // Handle epic_id change - triggers cascading KPI reload
      else if (field === 'epic_id') {
        // Use the handleEpicChange function to properly handle cascading effects
        handleEpicChange(value);
        return; // handleEpicChange sets hasChanges itself
      }
      // Regular field update (skip special operation fields)
      else if (!field.startsWith('_')) {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
    });
    
    setHasChanges(true);
  }, [personas, statuses, handleEpicChange]);
  
  // Handle AI Co-Pilot split suggestion
  const handleSplitItem = useCallback((splitPayload) => {
    if (!splitPayload?.feature_1 || !splitPayload?.feature_2) return;
    setSplitData(splitPayload);
    setShowSplitModal(true);
  }, []);
  
  // Refresh feature data after changes
  const refreshFeatureData = useCallback(async () => {
    if (!feature?.feature_id) return;
    try {
      const res = await featuresApi.get(feature.feature_id);
      if (res.data) {
        setFormData({
          title: res.data.title || '',
          description: res.data.description || '',
          user_story: res.data.user_story || '',
          requirements: res.data.requirements || [],
          priority_level: res.data.priority_level || 'Medium',
          status: res.data.status || 'Backlog',
          persona_ids: res.data.persona_ids || [],
          assignee_id: res.data.assignee_id || '',
          due_date: res.data.due_date ? res.data.due_date.split('T')[0] : '',
          effort_size: res.data.effort_size || '',
          tag_ids: res.data.tag_ids || [],
          links: res.data.links || [],
          epic_id: res.data.epic_id || '',
          execution_type: res.data.execution_type || 'engineering',
          is_blocked: res.data.is_blocked || false,
          blocker_reason: res.data.blocker_reason || '',
          blocked_at: res.data.blocked_at || null,
          blocked_by: res.data.blocked_by || null,
          blocked_by_name: res.data.blocked_by_name || null,
          release_id: res.data.release_id || '',
          kpi_alignments: res.data.kpi_alignments || []
        });
      }
    } catch (err) {
      console.error('Failed to refresh feature:', err);
    }
  }, [feature?.feature_id]);
  
  // Track which feature ID has been initialized — prevents re-initialization on prop reference changes
  const initializedForIdRef = useRef(null);

  // Load feature data — ONCE per modal open session (guarded by feature ID)
  useEffect(() => {
    const currentId = feature?.feature_id;
    if (!currentId || !isOpen) return;
    // Only initialize if this is a new feature (different ID) — not a prop reference change
    if (initializedForIdRef.current === currentId) return;
    initializedForIdRef.current = currentId;

    const featureData = {
      title: feature.title || '',
      description: feature.description || '',
      user_story: feature.user_story || '',
      requirements: feature.requirements || [],
      priority_level: feature.priority_level || 'Medium',
      status: feature.status || 'Backlog',
      release_id: feature.release_id || '',
      persona_ids: feature.persona_ids || [],
      assignee_id: feature.assignee_id || '',
      due_date: feature.due_date || '',
      effort_size: feature.effort_size || '',
      tag_ids: feature.tag_ids || [],
      links: feature.links || [],
      epic_id: feature.epic_id || '',
      execution_type: feature.execution_type || 'engineering',
      is_blocked: feature.is_blocked || false,
      blocker_reason: feature.blocker_reason || '',
      blocked_at: feature.blocked_at || null,
      blocked_by: feature.blocked_by || null,
      blocked_by_name: feature.blocked_by_name || null,
      kpi_alignments: feature.kpi_alignments || []
    };
    setFormData(featureData);
    setHasChanges(false);
    
    // Store original epic_id and kpi_alignments for epic change handling
    setOriginalEpicId(feature.epic_id || '');
    setOriginalKpiAlignments(feature.kpi_alignments || []);
    setAvailableKpisForEpic(null);
    
    // Reset edit mode to View Mode (default) when opening
    setIsEditMode(false);
    
    // Reset active tab to Key Attributes
    setActiveTab('key-attributes');
    
    // Clear "NEW" badge if this is a split feature
    if (feature.is_new_split) {
      featuresApi.markViewed(feature.feature_id).catch(() => {});
    }
  }, [feature, isOpen]);

  // Reset initialization guard when modal closes
  useEffect(() => {
    if (!isOpen) {
      initializedForIdRef.current = null;
    }
  }, [isOpen]);
  
  // Load tags for this product
  useEffect(() => {
    const loadTags = async () => {
      // Try to get product_id from productContext, or from the feature's epic
      const productId = productContext?.product_id;
      if (!productId) {
        console.log('[FeatureDetail] No product_id available for loading tags');
        return;
      }
      try {
        // Load product tags (excludes initiative tags)
        const res = await productTagsApi.list(productId);
        setAvailableTags(res.data?.tags || []);
        
        // Load initiative tags
        const initRes = await productTagsApi.listInitiativeTags(productId);
        console.log('[FeatureDetail] Initiative tags loaded:', initRes.data);
        setInitiativeTags(initRes.data?.tags || []);
        setInitiativeTagsGrouped(initRes.data?.by_initiative || []);
      } catch (err) {
        console.error('Error loading tags:', err);
      }
    };
    loadTags();
  }, [productContext?.product_id]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!feature?.feature_id) return;
    
    // If feature has a linked ticket, ask before saving
    if (feature?.external_ticket?.key && !showTicketSyncModal) {
      setPendingSaveAction('save');
      setShowTicketSyncModal(true);
      return;
    }
    
    await executeSave(false, false);
  };

  const handleSaveAndClose = async () => {
    if (!feature?.feature_id) return;
    
    // If feature has a linked ticket, ask before saving
    if (feature?.external_ticket?.key && !showTicketSyncModal) {
      setPendingSaveAction('saveAndClose');
      setShowTicketSyncModal(true);
      return;
    }
    
    await executeSave(true, false);
  };

  const handleTicketSyncChoice = async (syncTicket) => {
    setShowTicketSyncModal(false);
    const shouldClose = pendingSaveAction === 'saveAndClose';
    setPendingSaveAction(null);
    await executeSave(shouldClose, syncTicket);
  };

  const executeSave = async (shouldClose, syncTicket) => {
    if (!feature?.feature_id) return;
    
    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        requirements: formData.requirements.map(req => {
          if (typeof req === 'string') {
            return { text: req, is_completed: false };
          }
          return req;
        }),
        ...(syncTicket ? { sync_ticket: true } : {})
      };
      
      const response = await featuresApi.update(feature.feature_id, dataToSave);
      const savedFeature = response.data || { ...feature, ...dataToSave };
      setHasChanges(false);
      
      if (!standalone) {
        if (parentRefresh?.feature) {
          parentRefresh.feature();
          parentRefresh.markDirty?.('features');
        } else if (onSave) {
          await onSave(savedFeature);
        } else if (onUpdate) {
          await onUpdate(feature.feature_id);
        }
      }
      
      // Show appropriate toast
      if (syncTicket && savedFeature._ticket_sync_result) {
        if (savedFeature._ticket_sync_result.success) {
          toast.success(`Feature saved & ticket ${feature.external_ticket.key} updated`);
        } else {
          toast.success('Feature saved');
          toast.error(`Ticket update failed: ${savedFeature._ticket_sync_result.error}`);
        }
      } else {
        toast.success('Feature saved');
      }
      
      if (shouldClose) {
        performClose();
      }
    } catch (err) {
      toast.error('Failed to save feature');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };
  
  // Delete feature
  const handleDelete = async () => {
    if (!feature?.feature_id) return;
    
    setDeleting(true);
    try {
      await featuresApi.delete(feature.feature_id);
      toast.success('Feature deleted');
      setShowDeleteConfirm(false);
      
      // STANDALONE MODE: Skip parent callback, just close
      if (!standalone) {
        if (parentRefresh?.feature) {
          parentRefresh.feature();
          parentRefresh.markDirty?.('features');
        } else if (onDelete) {
          await onDelete();
        } else if (onUpdate) {
          await onUpdate();
        }
      }
      onClose();
    } catch (err) {
      toast.error('Failed to delete feature');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };
  
  // Tag management
  const addTag = (tag) => {
    if (!formData.tag_ids.includes(tag.tag_id)) {
      handleChange('tag_ids', [...formData.tag_ids, tag.tag_id]);
    }
  };
  
  const removeTag = (tagId) => {
    handleChange('tag_ids', formData.tag_ids.filter(id => id !== tagId));
  };
  
  // Count visible KPI alignments (only those currently in available KPIs)
  // This reflects what's shown in the DOM, not all stored alignments
  
  // Acceptance criteria
  const addRequirement = () => {
    if (newRequirement.trim()) {
      handleChange('requirements', [...formData.requirements, newRequirement.trim()]);
      setNewCriterion('');
    }
  };
  
  const removeRequirement = (index) => {
    handleChange('requirements', formData.requirements.filter((_, i) => i !== index));
  };
  
  // Links - normalize to {url, title, tags} objects for backward compat
  const normalizeLink = (link) => {
    if (typeof link === 'string') return { url: link, title: '', tags: [] };
    return { url: link.url || '', title: link.title || '', tags: link.tags || [] };
  };

  const addLink = () => {
    if (newLink.trim()) {
      const normalized = formData.links.map(normalizeLink);
      handleChange('links', [...normalized, { url: newLink.trim(), title: '', tags: [] }]);
      setNewLink('');
    }
  };
  
  const removeLink = (index) => {
    handleChange('links', formData.links.filter((_, i) => i !== index));
  };

  const updateLink = (index, updates) => {
    const normalized = formData.links.map(normalizeLink);
    normalized[index] = { ...normalized[index], ...updates };
    handleChange('links', normalized);
  };
  
  // Handle split completion - create log entries for both new features
  const handleSplitComplete = async (newFeatures) => {
    toast.success('Feature split into 2 new features!');
    setShowSplitModal(false);
    setSplitData(null);
    
    // STANDALONE MODE: Skip parent callback
    if (!standalone) {
      if (parentRefresh?.feature) {
        parentRefresh.feature();
        parentRefresh.markDirty?.('features');
      } else {
        onUpdate?.(); // Refresh parent view
      }
    }
    onClose(); // Close the detail modal
  };
  
  // Handle modal close - check for unsaved changes and pending AI actions
  const handleModalClose = () => {
    // First check for unsaved form changes
    if (hasChanges) {
      setShowUnsavedWarning(true);
      return;
    }
    // No longer need close guard - pending actions persist in chat log
    performClose();
  };
  
  const performClose = () => {
    // Clear tag state
    setTagSearch('');
    setShowTagModal(false);
    setNewTagName('');
    setTagModalTab('product');
    setSelectedInitiativeFilter('all');
    
    // Close the modal
    onClose();
  };
  
  // Get tag from either product tags or initiative tags
  const getTagById = (tagId) => {
    return availableTags.find(t => t.tag_id === tagId) || 
           initiativeTags.find(t => t.tag_id === tagId);
  };
  const filteredTags = availableTags.filter(t => 
    !formData.tag_ids.includes(t.tag_id) &&
    t.name.toLowerCase().includes(tagSearch.toLowerCase())
  );
  
  // Filter initiative tags based on selected initiative filter and search
  const filteredInitiativeTags = initiativeTags.filter(t => {
    const matchesSearch = !tagSearch || t.name.toLowerCase().includes(tagSearch.toLowerCase());
    const matchesFilter = selectedInitiativeFilter === 'all' || t.initiative_id === selectedInitiativeFilter;
    return matchesSearch && matchesFilter;
  });
  
  if (!isOpen || !feature) {
    return null;
  }
  
  return (
    <>
    <ModalZIndexProvider zIndex={zIndex}>
    <div className="fixed inset-0" style={{ zIndex }}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Modal - red border when blocked */}
      <div className={cn(
        "absolute inset-4 md:inset-8 lg:inset-12 rounded-xl shadow-2xl flex flex-col overflow-hidden",
        colors.bgCard,
        formData.is_blocked && "ring-2 ring-red-500 animate-pulse-subtle"
      )}>
        {/* Header */}
        <div className={cn("flex items-center justify-between px-6 py-4 border-b", colors.borderPrimary)}>
          <div className="flex items-center gap-3">
            {/* Blocked indicator next to title */}
            {formData.is_blocked && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-xs font-semibold text-red-600 dark:text-red-400">Blocked</span>
              </div>
            )}
            {isInProduction && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700">
                <Lock className="w-4 h-4 text-green-600" />
                <span className="text-xs font-semibold text-green-600 dark:text-green-400">In Production</span>
              </div>
            )}
            {/* External ticket indicator (GitHub, JIRA, Linear, etc.) */}
            {feature?.external_ticket?.key && (
              <a
                href={feature.external_ticket.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-800 dark:bg-gray-700 text-white hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                title={`View ${feature.external_ticket.provider === 'github_issues' ? 'GitHub Issue' : 'Ticket'} ${feature.external_ticket.key}`}
              >
                <Github className="w-4 h-4" />
                <span className="text-xs font-semibold">{feature.external_ticket.key}</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {/* Ticket push failed - retry button */}
            {feature?.external_ticket_push_failed && !feature?.external_ticket?.key && (
              <button
                onClick={async () => {
                  try {
                    toast.loading('Retrying ticket creation...', { id: 'retry-ticket' });
                    await releasesApi.pushTicket(feature.feature_id);
                    toast.success('Ticket created!', { id: 'retry-ticket' });
                    // Refresh feature data
                    const res = await featuresApi.get(feature.feature_id);
                    if (res.data) {
                      setFormData(prev => ({ ...prev, ...res.data }));
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to create ticket', { id: 'retry-ticket' });
                  }
                }}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                title={`Ticket push failed: ${feature.external_ticket_push_error || 'Unknown error'}. Click to retry.`}
              >
                <Github className="w-4 h-4 text-red-500" />
                <RefreshCw className="w-3 h-3 text-red-500" />
                <span className="text-xs font-semibold text-red-600 dark:text-red-400">Push Failed</span>
              </button>
            )}
            <div>
              <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>
                {isInProduction ? 'View Feature (Locked)' : isEditing ? 'Edit Feature' : 'View Feature'}
              </h2>
              {formData.title && (
                <p className={cn("text-sm truncate max-w-[400px]", colors.textMuted)}>{formData.title}</p>
              )}
            </div>
            {/* Edit Mode Toggle Switch - only visible to PM/Admin when status is not Backlog */}
            {showEditToggle && (
              <div className="flex items-center gap-2">
                <span className={cn("text-xs", colors.textMuted)}>View</span>
                <button
                  onClick={() => setIsEditMode(!isEditMode)}
                  className={cn(
                    "relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                    isEditMode ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
                  )}
                  role="switch"
                  aria-checked={isEditMode}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out",
                      isEditMode ? "translate-x-5" : "translate-x-0"
                    )}
                  />
                </button>
                <span className={cn("text-xs", isEditMode ? "text-blue-600 dark:text-blue-400 font-medium" : colors.textMuted)}>Edit</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditing && !isInProduction && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                title="Delete feature"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !hasChanges || isInProduction}
              variant={hasChanges && !isInProduction ? "default" : "outline"}
              className={cn(
                hasChanges && !isInProduction ? colors.accentGradient : "opacity-50 cursor-not-allowed"
              )}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="ml-1">Save</span>
            </Button>
            <Button
              size="sm"
              onClick={handleSaveAndClose}
              disabled={saving || !hasChanges || isInProduction}
              variant={hasChanges && !isInProduction ? "default" : "outline"}
              className={cn(
                hasChanges && !isInProduction ? colors.accentGradient : "opacity-50 cursor-not-allowed"
              )}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="ml-1">Save & Close</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleModalClose}
              className={cn(colors.textMuted, "hover:bg-gray-200 dark:hover:bg-gray-700")}
            >
              Close
            </Button>
          </div>
        </div>
      
        {/* Content - 60/40 Split (or full width if no AI access or view mode) */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Form Fields (60% when AI shown, 100% otherwise) */}
          <div className={cn(
            "overflow-y-auto flex flex-col",
            (canUseAICopilot && !suppressChildModals && isEditing) ? "w-[60%] border-r" : "w-full",
            colors.borderPrimary
          )}>
          {/* Collaborator Warning Banner */}
          {isCollaborator && !isInProduction && (
            <div className={cn("mx-6 mt-4 p-3 rounded-lg border flex items-center gap-2", "bg-amber-500/10 border-amber-500/30")}>
              <Lock className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-600 dark:text-amber-400">
                You have view-only access. You can update status, assignee, and mark requirements complete.
              </p>
            </div>
          )}
          
          {/* In Production Locked Banner */}
          {isInProduction && (
            <div className={cn("mx-6 mt-4 p-3 rounded-lg border flex items-center gap-2", "bg-green-500/10 border-green-500/30")}>
              <Lock className="w-4 h-4 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-600 dark:text-green-400">
                <strong>Locked</strong> - This feature has been deployed to production. All fields are read-only to preserve the historical record.
              </p>
            </div>
          )}
          
          {/* View Mode Banner for PM/Admin */}
          {canEdit && !isEditing && showEditToggle && (
            <div className={cn("mx-6 mt-4 p-3 rounded-lg border flex items-center gap-2", "bg-blue-500/10 border-blue-500/30")}>
              <Eye className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <p className="text-sm text-blue-600 dark:text-blue-400">
                View mode - Status, assignee, and requirements are editable. Toggle <strong>Edit</strong> above for full editing.
              </p>
            </div>
          )}
          
          {/* Tab Navigation - Pill Style */}
          <div className="px-6 pt-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={cn("mb-4 border inline-flex", colors.bgCard, colors.borderPrimary)}>
                <TabsTrigger 
                  value="key-attributes" 
                  className={cn(
                    "data-[state=active]:shadow-sm transition-all text-sm",
                    colors.tabActive,
                    formData.is_blocked && activeTab !== 'key-attributes' && "text-red-500 border border-red-400 bg-red-50 dark:bg-red-900/20"
                  )}
                >
                  Key Attributes
                </TabsTrigger>
                <TabsTrigger value="overview" className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}>
                  Overview
                </TabsTrigger>
                {isEnabled(FEATURE_IDS.TAB_GOALS) && isEnabled(FEATURE_IDS.TAB_INITIATIVES) && (
                <TabsTrigger 
                  value="alignment" 
                  className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                  data-testid="tab-alignment"
                >
                  KPIs {(formData.kpi_alignments?.length > 0) && `(${formData.kpi_alignments.length})`}
                </TabsTrigger>
                )}
                <TabsTrigger value="requirements" className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}>
                  Requirements {(formData.requirements?.length > 0) && `(${formData.requirements.length})`}
                </TabsTrigger>
                <TabsTrigger value="references" className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}>
                  References {(formData.links?.length > 0) && `(${formData.links.length})`}
                </TabsTrigger>
                <TabsTrigger value="attachments" className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}>
                  Attachments {attachmentCount > 0 && `(${attachmentCount})`}
                </TabsTrigger>
                <TabsTrigger value="notes" className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}>
                  Notes {notesCount > 0 && `(${notesCount > 9 ? '9+' : notesCount})`}
                </TabsTrigger>
                <TabsTrigger value="devlog" className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}>
                  Dev Log
                </TabsTrigger>
                <TabsTrigger value="history" className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}>
                  History
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            
            {/* ===== KEY ATTRIBUTES TAB ===== */}
            {activeTab === 'key-attributes' && (
              <>
                {/* Title (Full Width) */}
                <div>
                  <Label className={colors.textSecondary}>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    disabled={!isEditing}
                    className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, !isEditing && "opacity-70 cursor-not-allowed")}
                  />
                </div>
                
                {/* Tags (Full Width) */}
                <div>
                  <Label className={colors.textSecondary}>Tags</Label>
                  <div 
                    className={cn(
                      "mt-1 flex flex-wrap items-center gap-2 min-h-[38px] p-2 rounded-lg border",
                      colors.bgInput, colors.borderPrimary, 
                      !isEditing && "opacity-70 cursor-not-allowed"
                    )}
                  >
                    {formData.tag_ids?.map(tagId => {
                      const tag = getTagById(tagId);
                      return tag ? (
                        <span
                          key={tagId}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0"
                          style={{ 
                            backgroundColor: tag.color,
                            color: getContrastColor(tag.color)
                          }}
                        >
                          {tag.name}
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => removeTag(tagId)}
                              className="ml-0.5 hover:bg-black/10 rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      ) : null;
                    })}
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => setShowTagModal(true)}
                        className={cn(
                          "inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border border-dashed transition-colors",
                          colors.borderPrimary, colors.textMuted,
                          "hover:border-blue-400 hover:text-blue-500"
                        )}
                      >
                        <Plus className="w-3 h-3" />
                        {formData.tag_ids?.length ? "Add more..." : "Add tags..."}
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Epic & Assignee Row */}
                <div className={cn("grid gap-4", isEnabled(FEATURE_IDS.ORG_TEAM) ? "grid-cols-2" : "grid-cols-1")}>
                  <div>
                    <Label className={colors.textSecondary}>Epic</Label>
                    <Select 
                      value={formData.epic_id || ''} 
                      onValueChange={handleEpicChange}
                      disabled={!isEditing}
                    >
                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, !isEditing && "opacity-70 cursor-not-allowed")}>
                        <SelectValue placeholder="Select epic" />
                      </SelectTrigger>
                      <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                        {epicContext?.epics?.filter(e => e.status !== 'archived').map(e => (
                          <SelectItem key={e.epic_id} value={e.epic_id}>{e.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isEnabled(FEATURE_IDS.ORG_TEAM) && (
                  <div>
                    <Label className={colors.textSecondary}>Assignee</Label>
                    <Select 
                      value={formData.assignee_id || 'none'} 
                      onValueChange={(v) => handleChange('assignee_id', v === 'none' ? '' : v)}
                      disabled={isInProduction}
                    >
                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, isInProduction && "opacity-70 cursor-not-allowed")}>
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {teamMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  )}
                </div>
                
                {/* Release & Status Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={colors.textSecondary}>Release</Label>
                    <Select 
                      value={formData.release_id || 'parking-lot'} 
                      onValueChange={(v) => handleChange('release_id', v === 'parking-lot' ? '' : v)}
                      disabled={isInProduction}
                    >
                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, isInProduction && "opacity-70 cursor-not-allowed")}>
                        <SelectValue placeholder="Parking Lot" />
                      </SelectTrigger>
                      <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                        <SelectItem value="parking-lot">Parking Lot</SelectItem>
                        {releases
                          .sort((a, b) => new Date(a.target_date || 0) - new Date(b.target_date || 0))
                          .map(r => (
                            <SelectItem key={r.release_id} value={r.release_id}>{r.name}</SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={colors.textSecondary}>Status</Label>
                    <Select 
                      value={formData.status || 'Backlog'} 
                      onValueChange={(v) => handleChange('status', v)}
                      disabled={isInProduction}
                    >
                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, isInProduction && "opacity-70 cursor-not-allowed")}>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                        {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Priority & Effort Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={colors.textSecondary}>Priority</Label>
                    <Select 
                      value={formData.priority_level || 'Medium'} 
                      onValueChange={(v) => handleChange('priority_level', v)}
                      disabled={!isEditing}
                    >
                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, !isEditing && "opacity-70 cursor-not-allowed")}>
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                        {PRIORITY_LEVELS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={colors.textSecondary}>Effort</Label>
                    <Select 
                      value={formData.effort_size || undefined} 
                      onValueChange={(v) => handleChange('effort_size', v)}
                      disabled={!isEditing}
                    >
                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, !isEditing && "opacity-70 cursor-not-allowed")}>
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                        {EFFORT_SIZES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Execution Type + Due Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                      Execution Type
                    </Label>
                    <Select 
                      value={formData.execution_type || 'engineering'} 
                      onValueChange={(v) => handleChange('execution_type', v)}
                      disabled={!isEditing}
                    >
                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, !isEditing && "opacity-70 cursor-not-allowed")} data-testid="execution-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                        <SelectItem value="engineering">Engineering / Development</SelectItem>
                        <SelectItem value="strategic">Strategic / Operational</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                      <Calendar className="w-3 h-3" /> Due Date
                    </Label>
                    <div className="mt-1">
                      <DatePickerWithMarkers
                        value={formData.due_date || ''}
                        onChange={(date) => handleChange('due_date', date)}
                        parentDeadlines={parentDeadlines}
                        placeholder="Select due date"
                        disabled={!isEditing}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Blocked Toggle */}
                <div className={cn(
                  "p-4 rounded-lg border mt-4",
                  formData.is_blocked 
                    ? "bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700" 
                    : cn(colors.bgTertiary, colors.borderPrimary)
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className={cn("w-5 h-5", formData.is_blocked ? "text-red-500" : colors.textMuted)} />
                      <div>
                        <Label className={cn("text-sm font-medium", formData.is_blocked ? "text-red-700 dark:text-red-400" : colors.textPrimary)}>
                          Blocked
                        </Label>
                        <p className={cn("text-xs mt-0.5", formData.is_blocked ? "text-red-600 dark:text-red-400" : colors.textMuted)}>
                          {formData.is_blocked 
                            ? (formData.blocked_at 
                                ? `Set by ${getBlockedByName() || 'MCP'} on ${formatDate(formData.blocked_at)} at ${formatTime(formData.blocked_at)}`
                                : 'Feature is currently blocked')
                            : 'Mark this feature as blocked if work cannot proceed'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.is_blocked || false}
                      onCheckedChange={(checked) => {
                        handleChange('is_blocked', checked);
                        if (checked) {
                          handleChange('blocked_at', new Date().toISOString());
                          handleChange('blocked_by', user?.user_id);
                          handleChange('blocked_by_name', user?.name || user?.email);
                        } else {
                          handleChange('blocked_at', null);
                          handleChange('blocked_by', null);
                          handleChange('blocked_by_name', null);
                          handleChange('blocker_reason', '');
                        }
                      }}
                      disabled={!isEditing}
                      className={formData.is_blocked ? "data-[state=checked]:bg-red-500" : ""}
                    />
                  </div>
                  
                  {formData.is_blocked && (
                    <div className="mt-3">
                      <Label className={cn("text-xs", "text-red-600 dark:text-red-400")}>Blocker Reason</Label>
                      <Textarea
                        value={formData.blocker_reason || ''}
                        onChange={(e) => handleChange('blocker_reason', e.target.value)}
                        placeholder="Describe what's blocking this feature..."
                        disabled={!isEditing}
                        rows={2}
                        className={cn(
                          "mt-1 text-sm",
                          "bg-white dark:bg-gray-800 border-red-300 dark:border-red-700",
                          colors.textPrimary,
                          !isEditing && "opacity-70 cursor-not-allowed"
                        )}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* ===== OVERVIEW TAB ===== */}
            {activeTab === 'overview' && (
              <>
                {/* Personas (Full Width) - gated by feature flag */}
                {isEnabled(FEATURE_IDS.TAB_PERSONAS) && (
                <div>
                  <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                    <Users className="w-3 h-3" /> Personas
                  </Label>
                  <div 
                    className={cn(
                      "mt-1 flex flex-wrap items-center gap-2 min-h-[38px] p-2 rounded-lg border cursor-text",
                      colors.bgInput, colors.borderPrimary, 
                      !isEditing && "opacity-70 cursor-not-allowed"
                    )}
                  >
                    {(formData.persona_ids || []).map(personaId => {
                      const persona = personas.find(p => p.persona_id === personaId);
                      return persona ? (
                        <span
                          key={personaId}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                        >
                          {persona.name}
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => handleChange('persona_ids', formData.persona_ids.filter(id => id !== personaId))}
                              className="ml-0.5 hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      ) : null;
                    })}
                    {isEditing && (
                      <Select 
                        value=""
                        onValueChange={(v) => {
                          if (v && !formData.persona_ids.includes(v)) {
                            handleChange('persona_ids', [...formData.persona_ids, v]);
                          }
                        }}
                      >
                        <SelectTrigger className="w-auto min-w-[100px] h-7 text-xs border-0 bg-transparent focus:ring-0 p-0">
                          <SelectValue placeholder={formData.persona_ids?.length ? "Add more..." : "Add personas..."} />
                        </SelectTrigger>
                        <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                          {personas
                            .filter(p => !formData.persona_ids?.includes(p.persona_id))
                            .map(p => (
                              <SelectItem key={p.persona_id} value={p.persona_id}>{p.name}</SelectItem>
                            ))
                          }
                          {personas.filter(p => !formData.persona_ids?.includes(p.persona_id)).length === 0 && (
                            <div className="px-2 py-1.5 text-xs text-gray-500">All personas selected</div>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                )}
                
                {/* Description */}
                <div>
                  <Label className={colors.textSecondary}>Description</Label>
                  <div className="mt-1">
                    <MarkdownEditor
                      content={formData.description || ''}
                      onChange={(value) => handleChange('description', value)}
                      placeholder="Describe this feature..."
                      minHeight={180}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                
                {/* User Story */}
                <div>
                  <Label className={colors.textSecondary}>User Story</Label>
                  <div className="mt-1">
                    <MarkdownEditor
                      content={formData.user_story || ''}
                      onChange={(value) => handleChange('user_story', value)}
                      placeholder="As a [user], I want [goal] so that [benefit]..."
                      minHeight={150}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* ===== KPI ALIGNMENTS TAB (Outcome-Driven Model) ===== */}
            {activeTab === 'alignment' && isEnabled(FEATURE_IDS.TAB_GOALS) && isEnabled(FEATURE_IDS.TAB_INITIATIVES) && (
              <KpiSubscriptionTab
                entityType="feature"
                entityId={feature?.feature_id}
                disabled={isInProduction}
                fetchInheritedKpis={async (id) => {
                  const response = await featuresApi.getInheritedKpis(id);
                  return response.data;
                }}
                currentAlignments={formData.kpi_alignments || []}
                onChange={(alignments) => {
                  setFormData(prev => ({
                    ...prev,
                    kpi_alignments: alignments
                  }));
                  setHasChanges(true);
                }}
                subscriptionFieldName="kpi_alignments"
                showHypothesis={true}
                overrideData={availableKpisForEpic}
              />
            )}
            
            {/* ===== REQUIREMENTS TAB ===== */}
            {activeTab === 'requirements' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className={colors.textSecondary}>Acceptance Criteria / Requirements</Label>
                  {formData.requirements.length > 0 && (
                    <span className={cn("text-xs px-2 py-0.5 rounded-full", colors.bgTertiary, colors.textMuted)}>
                      ✓ {formData.requirements.filter(r => typeof r === 'object' ? r.is_completed : false).length}/{formData.requirements.length} complete
                    </span>
                  )}
                </div>
                <div className="mt-2 space-y-2">
                  {formData.requirements.map((criterion, i) => {
                    const reqText = typeof criterion === 'string' ? criterion : (criterion?.text || criterion);
                    const isCompleted = typeof criterion === 'object' ? criterion.is_completed : false;
                    
                    return (
                      <div key={i} className={cn("flex items-center gap-3 p-3 rounded-lg", colors.bgTertiary, isCompleted && "opacity-70")}>
                        {/* Checkbox - enabled for all users except when in production */}
                        <button
                          onClick={() => {
                            if (isInProduction) return;
                            const updated = [...formData.requirements];
                            if (typeof updated[i] === 'string') {
                              updated[i] = { text: updated[i], is_completed: true };
                            } else {
                              updated[i] = { ...updated[i], is_completed: !updated[i].is_completed };
                            }
                            handleChange('requirements', updated);
                          }}
                          disabled={isInProduction}
                          className={cn(
                            "w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                            isCompleted 
                              ? "bg-green-500 border-green-500 text-white" 
                              : cn("border-gray-300 dark:border-gray-600 hover:border-green-400"),
                            isInProduction && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          {isCompleted && <Check className="w-3 h-3" />}
                        </button>
                        
                        {/* Requirement text - editable in edit mode */}
                        {isEditing ? (
                          <Input
                            value={reqText}
                            onChange={(e) => {
                              const updated = [...formData.requirements];
                              if (typeof updated[i] === 'string') {
                                updated[i] = e.target.value;
                              } else {
                                updated[i] = { ...updated[i], text: e.target.value };
                              }
                              handleChange('requirements', updated);
                            }}
                            className={cn(
                              "flex-1 text-sm",
                              colors.bgInput, colors.borderPrimary, colors.textPrimary,
                              isCompleted && "line-through opacity-60"
                            )}
                          />
                        ) : (
                          <span className={cn("flex-1 text-sm", colors.textPrimary, isCompleted && "line-through")}>
                            {reqText}
                          </span>
                        )}
                        
                        {/* Delete button - only in edit mode */}
                        {isEditing && (
                          <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={() => removeRequirement(i)}>
                            <X className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                  {formData.requirements.length === 0 && (
                    <p className={cn("text-sm py-4 text-center", colors.textMuted)}>
                      No requirements defined yet
                    </p>
                  )}
                  {/* Add new requirement - only in edit mode */}
                  {isEditing && (
                    <div className="flex gap-2 mt-4">
                      <Input
                        value={newRequirement}
                        onChange={(e) => setNewCriterion(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addRequirement()}
                        placeholder="Add acceptance criterion..."
                        className={cn(colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                      />
                      <Button variant="outline" size="icon" onClick={addRequirement} className={cn("border", colors.borderPrimary)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* ===== REFERENCES TAB ===== */}
            {activeTab === 'references' && (
              <div>
                <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                  <Link2 className="w-3 h-3" /> External Links & References
                </Label>
                <div className="mt-2 space-y-2">
                  {formData.links.map((rawLink, i) => {
                    const link = typeof rawLink === 'string' ? { url: rawLink, title: '', tags: [] } : rawLink;
                    return (
                      <EditableLinkRow
                        key={i}
                        link={link}
                        index={i}
                        isEditing={isEditing}
                        onUpdate={updateLink}
                        onRemove={removeLink}
                        colors={colors}
                      />
                    );
                  })}
                  {formData.links.length === 0 && (
                    <p className={cn("text-sm py-4 text-center", colors.textMuted)}>
                      No links added yet
                    </p>
                  )}
                  {isEditing && (
                    <div className="flex gap-2 mt-4">
                      <Input
                        value={newLink}
                        onChange={(e) => setNewLink(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addLink()}
                        placeholder="Add link URL..."
                        className={cn(colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                      />
                      <Button variant="outline" size="icon" onClick={addLink} className={cn("border", colors.borderPrimary)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* ===== ATTACHMENTS TAB ===== */}
            <div style={{ display: activeTab === 'attachments' ? 'block' : 'none' }}>
              <AttachmentsPanel
                entityType="feature"
                entityId={feature?.feature_id}
                productId={productContext?.product_id}
                canEdit={isEditing}
                colors={colors}
                onCountChange={setAttachmentCount}
              />
            </div>
            
            {/* ===== NOTES TAB ===== */}
            <div style={{ display: activeTab === 'notes' ? 'block' : 'none' }}>
              {feature?.feature_id && (
                <NotesPanel 
                  entityType="feature"
                  entityId={feature.feature_id} 
                  teamMembers={teamMembers}
                  colors={colors}
                  onCountChange={setNotesCount}
                />
              )}
            </div>
            
            {/* ===== DEV LOG TAB ===== */}
            {activeTab === 'devlog' && feature?.feature_id && (
              <DevLogTab featureId={feature.feature_id} />
            )}
            
            {/* ===== HISTORY TAB ===== */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Release History</h3>
                
                {/* Current Release */}
                <div className={cn("p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className={cn("font-medium", colors.textPrimary)}>Current Release</span>
                  </div>
                  <p className={cn("text-sm ml-5", colors.textSecondary)}>
                    {releases.find(r => r.release_id === formData.release_id)?.name || 'Unassigned'}
                  </p>
                  {formData.in_production && (
                    <Badge className="ml-5 mt-2 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      In Production
                    </Badge>
                  )}
                </div>
                
                {/* Slippage Summary */}
                {((feature?.sprint_slippage_count || 0) > 0 || (feature?.release_slippage_count || 0) > 0) && (
                  <div className={cn("p-4 rounded-lg border", "bg-orange-50 dark:bg-orange-950/20", "border-orange-200 dark:border-orange-800")}>
                    <h4 className={cn("font-medium mb-2", colors.textPrimary)}>Slippage Summary</h4>
                    <div className="space-y-1 text-sm">
                      {(feature?.sprint_slippage_count || 0) > 0 && (
                        <p className={cn(colors.textSecondary)}>
                          <span className="font-medium text-orange-600">Sprint Slippage:</span> {feature.sprint_slippage_count}x - Feature was incomplete when sprint closed
                        </p>
                      )}
                      {(feature?.release_slippage_count || 0) > 0 && (
                        <p className={cn(colors.textSecondary)}>
                          <span className="font-medium text-yellow-600">Release Slippage:</span> {feature.release_slippage_count}x - Feature was Done but not promoted to production
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Original Release */}
                {feature?.original_release_id && feature.original_release_id !== formData.release_id && (
                  <div className={cn("p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                      <span className={cn("font-medium", colors.textPrimary)}>Originally Assigned</span>
                    </div>
                    <p className={cn("text-sm ml-5", colors.textSecondary)}>
                      {releases.find(r => r.release_id === feature.original_release_id)?.name || feature.original_release_id}
                    </p>
                  </div>
                )}
                
                {/* Slippage Timeline */}
                {feature?.slippage_history && feature.slippage_history.length > 0 ? (
                  <div>
                    <h4 className={cn("font-medium mb-3", colors.textPrimary)}>Movement Timeline</h4>
                    <div className="relative">
                      {/* Timeline line */}
                      <div className={cn("absolute left-[5px] top-3 bottom-3 w-0.5", colors.bgTertiary)}></div>
                      
                      <div className="space-y-4">
                        {feature.slippage_history.map((entry, idx) => (
                          <div key={idx} className="flex gap-3">
                            <div className={cn(
                              "w-3 h-3 rounded-full mt-1.5 flex-shrink-0 z-10",
                              entry.slippage_type === 'sprint' ? "bg-orange-500" : "bg-yellow-500"
                            )}></div>
                            <div className={cn("flex-1 p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-xs",
                                    entry.slippage_type === 'sprint' 
                                      ? "border-orange-400 text-orange-600" 
                                      : "border-yellow-400 text-yellow-600"
                                  )}
                                >
                                  {entry.slippage_type === 'sprint' ? 'Sprint Slippage' : 'Release Slippage'}
                                </Badge>
                                <span className={cn("text-xs", colors.textMuted)}>
                                  {formatDate(entry.slipped_at)}
                                </span>
                              </div>
                              <p className={cn("text-sm", colors.textSecondary)}>
                                Moved from <span className="font-medium">{entry.from_release_name}</span> to <span className="font-medium">{entry.to_release_name}</span>
                              </p>
                              <p className={cn("text-xs mt-1", colors.textMuted)}>
                                Status at time: {entry.feature_status_at_slip}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={cn("text-center py-8", colors.textMuted)}>
                    <p className="text-sm">No release movements recorded</p>
                    <p className="text-xs mt-1">This feature has remained in its original release</p>
                  </div>
                )}
              </div>
            )}
            
          </div>{/* End Tab Content */}
        </div>{/* End Left Panel */}
        
          {/* Right: AI Co-Pilot (40%) - Only shown for existing features with edit access */}
          {showAICopilot && isEditing && (
            <div className={cn("w-[40%] flex flex-col", colors.bgTertiary)}>
              <UnifiedCoPilot
                contextType="feature"
                contextId={feature?.feature_id}
                productId={productContext?.product_id}
                contextData={formData}
                relatedData={{
                  product: productContext,
                  epic: epicContext?.epic,
                  siblingFeatures: epicContext?.siblingFeatures,
                  personas: personas,
                  teamMembers: teamMembers
                }}
                onApplyUpdates={handleApplyUpdates}
                onSplitItem={handleSplitItem}
                canEditField={() => true}
                onRefresh={refreshFeatureData}
                variant="item"
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Split Feature Modal */}
      <SplitFeatureModal
        isOpen={showSplitModal}
        originalFeature={feature}
        splitData={splitData}
        productId={productContext?.product_id}
        linkableEntities={{
          epics: epicContext?.epics || [],
          releases: releases || [],
          personas: personas || [],
          tags: availableTags || [],
          teamMembers: teamMembers || [],
        }}
        onClose={() => setShowSplitModal(false)}
        onSplitComplete={(newFeatures) => {
          toast.success('Feature split into 2 new features!');
          setShowSplitModal(false);
          setSplitData(null);
          // Split creates 2 new features and archives 1 - need collection refresh
          if (!standalone) {
            if (parentRefresh?.feature) {
              parentRefresh.feature();
              parentRefresh.markDirty?.('features');
            } else if (onSplitCompleteCallback) {
              onSplitCompleteCallback(newFeatures);
            } else if (onDelete) {
              onDelete();
            } else if (onUpdate) {
              onUpdate();
            }
          }
          onClose();
        }}
      />
      
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[70]">
          <div className="fixed inset-0 bg-black/70" onClick={() => setShowDeleteConfirm(false)} />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className={cn("w-full max-w-md rounded-xl shadow-2xl p-6", colors.bgCard)}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-full bg-red-500/10">
                  <Trash2 className="w-5 h-5 text-red-500" />
                </div>
                <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Delete Feature</h3>
              </div>
              
              <p className={cn("mb-2", colors.textSecondary)}>
                Are you sure you want to delete this feature?
              </p>
              <p className={cn("text-sm mb-6 p-3 rounded-lg", colors.bgTertiary, colors.textPrimary)}>
                &quot;{feature?.title}&quot;
              </p>
              <p className={cn("text-sm mb-6", colors.textMuted)}>
                This action cannot be undone. All requirements and associated data will be permanently removed.
              </p>
              
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className={cn("border", colors.borderPrimary)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Unsaved Changes Warning */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 z-[70]">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowUnsavedWarning(false)} />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className={cn("w-full max-w-md rounded-xl shadow-2xl p-6", colors.bgCard)}>
              <h3 className={cn("text-lg font-semibold mb-2", colors.textPrimary)}>Unsaved Changes</h3>
              <p className={cn("mb-4", colors.textSecondary)}>
                You have unsaved changes. Do you want to save them before closing?
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowUnsavedWarning(false)}>
                  Cancel
                </Button>
                <Button variant="ghost" onClick={() => {
                  setShowUnsavedWarning(false);
                  setHasChanges(false);
                  performClose();
                }}>
                  Discard
                </Button>
                <Button 
                  onClick={async () => {
                    await handleSave();
                    setShowUnsavedWarning(false);
                    performClose();
                  }}
                  className={cn(colors.accentGradient, "text-white")}
                >
                  Save & Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Tags Modal */}
      {showTagModal && (
        <div className="fixed inset-0 z-[70]">
          <div className="fixed inset-0 bg-black/50" onClick={() => {
            setShowTagModal(false);
            setTagSearch('');
            setNewTagName('');
            setTagModalTab('product');
            setSelectedInitiativeFilter('all');
          }} />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className={cn("w-full max-w-lg max-h-[90vh] min-h-[70vh] flex flex-col rounded-xl shadow-2xl", colors.bgCard)}>
              {/* Header */}
              <div className={cn("flex items-center justify-between p-4 border-b", colors.borderPrimary)}>
                <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Manage Tags</h3>
                <button 
                  onClick={() => {
                    setShowTagModal(false);
                    setTagSearch('');
                    setNewTagName('');
                    setTagModalTab('product');
                    setSelectedInitiativeFilter('all');
                  }}
                  className={cn("p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700", colors.textMuted)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Tabs */}
              <div className={cn("flex border-b", colors.borderPrimary)}>
                <button
                  onClick={() => setTagModalTab('product')}
                  className={cn(
                    "flex-1 px-4 py-2.5 text-sm font-medium transition-colors",
                    tagModalTab === 'product'
                      ? cn("border-b-2 border-blue-500", colors.textPrimary)
                      : cn(colors.textMuted, "hover:text-gray-700 dark:hover:text-gray-300")
                  )}
                >
                  Product Tags
                </button>
                <button
                  onClick={() => setTagModalTab('initiative')}
                  className={cn(
                    "flex-1 px-4 py-2.5 text-sm font-medium transition-colors",
                    tagModalTab === 'initiative'
                      ? cn("border-b-2 border-blue-500", colors.textPrimary)
                      : cn(colors.textMuted, "hover:text-gray-700 dark:hover:text-gray-300")
                  )}
                >
                  Initiative Tags
                  {initiativeTags.length > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                      {initiativeTags.length}
                    </span>
                  )}
                </button>
              </div>
              
              {/* Search */}
              <div className="p-4">
                <div className="relative">
                  <Tag className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
                  <Input
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    placeholder="Search tags..."
                    className={cn("pl-9", colors.bgInput, colors.borderPrimary)}
                  />
                </div>
                
                {/* Initiative Filter (only shown on initiative tab) */}
                {tagModalTab === 'initiative' && initiativeTagsGrouped.length > 0 && (
                  <div className="mt-3">
                    <select
                      value={selectedInitiativeFilter}
                      onChange={(e) => setSelectedInitiativeFilter(e.target.value)}
                      className={cn(
                        "w-full px-3 py-2 rounded-lg text-sm",
                        colors.bgInput, colors.borderPrimary, colors.textPrimary,
                        "focus:outline-none focus:ring-2 focus:ring-blue-500"
                      )}
                    >
                      <option value="all">All Initiatives</option>
                      {initiativeTagsGrouped.map(group => (
                        <option key={group.initiative_id} value={group.initiative_id}>
                          {group.initiative_title} ({group.tags.length})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              {/* Product Tags Tab Content */}
              {tagModalTab === 'product' && (
                <>
                  {/* Available Tags */}
                  <div className={cn("px-4 pb-2 overflow-y-auto flex-1 min-h-0")}>
                    <p className={cn("text-xs font-medium mb-2", colors.textMuted)}>Available Tags</p>
                    <div className="space-y-1">
                      {availableTags
                        .filter(t => !tagSearch || t.name.toLowerCase().includes(tagSearch.toLowerCase()))
                        .map(tag => {
                          const isSelected = formData.tag_ids?.includes(tag.tag_id);
                          return (
                            <button
                              key={tag.tag_id}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  removeTag(tag.tag_id);
                                } else {
                                  addTag(tag);
                                }
                              }}
                              className={cn(
                                "w-full px-3 py-2 text-left text-sm flex items-center justify-between rounded-lg transition-colors",
                                isSelected 
                                  ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" 
                                  : cn("hover:bg-gray-100 dark:hover:bg-gray-700", colors.bgTertiary)
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <span 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: tag.color }} 
                                />
                                <span className={colors.textPrimary}>{tag.name}</span>
                              </div>
                              {isSelected && (
                                <Check className="w-4 h-4 text-blue-500" />
                              )}
                            </button>
                          );
                        })
                      }
                      {availableTags.filter(t => !tagSearch || t.name.toLowerCase().includes(tagSearch.toLowerCase())).length === 0 && (
                        <p className={cn("text-sm text-center py-4", colors.textMuted)}>
                          {tagSearch ? `No tags matching "${tagSearch}"` : "No tags available"}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Create New Tag Section */}
                  <div className={cn("p-4 border-t", colors.borderPrimary)}>
                    <p className={cn("text-xs font-medium mb-2", colors.textMuted)}>Create New Tag</p>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Input
                          value={newTagName}
                          onChange={(e) => setNewTagName(e.target.value)}
                          placeholder="Tag name..."
                          className={cn(colors.bgInput, colors.borderPrimary)}
                        />
                      </div>
                      <input
                        type="color"
                        value={newTagColor}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                        title="Pick a color"
                      />
                      <Button
                        onClick={async () => {
                          if (!newTagName.trim() || !productContext?.product_id) return;
                          try {
                            const res = await productTagsApi.create(productContext.product_id, { 
                              name: newTagName.trim(),
                              color: newTagColor
                            });
                            if (res.data) {
                              setAvailableTags(prev => [...prev, res.data]);
                              handleChange('tag_ids', [...(formData.tag_ids || []), res.data.tag_id]);
                              toast.success(`Created tag "${newTagName.trim()}"`);
                              setNewTagName('');
                              setNewTagColor('#3B82F6');
                            }
                          } catch (err) {
                            toast.error('Failed to create tag');
                          }
                        }}
                        disabled={!newTagName.trim()}
                        size="sm"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
              
              {/* Initiative Tags Tab Content */}
              {tagModalTab === 'initiative' && (
                <div className={cn("px-4 pb-2 overflow-y-auto flex-1 min-h-0")}>
                  {filteredInitiativeTags.length === 0 ? (
                    <p className={cn("text-sm text-center py-8", colors.textMuted)}>
                      {initiativeTags.length === 0 
                        ? "No initiative tags available. Tags must be created from Initiatives that follow tags."
                        : tagSearch 
                          ? `No tags matching "${tagSearch}"`
                          : "No tags in the selected initiative"}
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {filteredInitiativeTags.map(tag => {
                        const isSelected = formData.tag_ids?.includes(tag.tag_id);
                        return (
                          <button
                            key={tag.tag_id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                removeTag(tag.tag_id);
                              } else {
                                addTag(tag);
                              }
                            }}
                            className={cn(
                              "w-full px-3 py-2 text-left text-sm flex items-center justify-between rounded-lg transition-colors",
                              isSelected 
                                ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" 
                                : cn("hover:bg-gray-100 dark:hover:bg-gray-700", colors.bgTertiary)
                            )}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span 
                                className="w-3 h-3 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: tag.color }} 
                              />
                              <div className="min-w-0">
                                <span className={cn("block truncate", colors.textPrimary)}>{tag.name}</span>
                                <span className={cn("text-xs truncate block", colors.textMuted)}>
                                  {tag.initiative_title}
                                </span>
                              </div>
                            </div>
                            {isSelected && (
                              <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Note about initiative tags */}
                  <div className={cn("mt-4 p-3 rounded-lg text-xs", colors.bgTertiary, colors.textMuted)}>
                    Initiative tags are managed from the Initiative's Tags tab. 
                    Select tags here to associate this feature with tag-following initiatives.
                  </div>
                </div>
              )}
              
              {/* Footer */}
              <div className={cn("flex justify-end p-4 border-t", colors.borderPrimary)}>
                <Button 
                  onClick={() => {
                    setShowTagModal(false);
                    setTagSearch('');
                    setNewTagName('');
                    setTagModalTab('product');
                    setSelectedInitiativeFilter('all');
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </ModalZIndexProvider>

    {/* Ticket Sync Confirmation Modal */}
    <Dialog open={showTicketSyncModal} onOpenChange={(open) => {
      if (!open) {
        setShowTicketSyncModal(false);
        setPendingSaveAction(null);
      }
    }}>
      <DialogContent className={cn("sm:max-w-md", colors.bgCard)}>
        <DialogHeader>
          <DialogTitle className={cn(colors.textPrimary)}>Update Linked Ticket?</DialogTitle>
          <DialogDescription>
            This feature is linked to ticket <strong>{feature?.external_ticket?.key}</strong>. Would you like to update the ticket with your latest changes?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => handleTicketSyncChoice(false)}
            data-testid="ticket-sync-no"
          >
            Save Only
          </Button>
          <Button
            onClick={() => handleTicketSyncChoice(true)}
            data-testid="ticket-sync-yes"
          >
            <Save className="w-4 h-4 mr-2" />
            Save & Update Ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default FeatureDetailModal;
